import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ProfilePageHeader } from '@sorare/core/src/components/navigation/ProfilePageHeader';
import { NBA_SCORES_GAMEID } from '@sorare/core/src/constants/__generated__/routes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { withRouteQuery } from '@sorare/core/src/lib/navigation/withRouteQuery';

import { MatchViewDialog } from '@sorare/gameplay/src/components/stats/MatchViewDialog';
import { PlayerGameScoreDrawer } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDrawer';
import { useAnySportEvents } from '@sorare/gameplay/src/lib/events';
import { MatchCenterPage } from '@sorare/gameplay/src/pages/MatchCenterPage';

import type {
  NBAScoresQuery,
  NBAScoresQueryVariables,
} from './__generated__/page.graphql';

const NBA_SCORES_QUERY = gql`
  query NBAScoresQuery {
    so5 {
      so5Fixtures(
        eventType: CLASSIC
        aasmStates: ["started", "closed"]
        sport: NBA
        first: 2
      ) {
        nodes {
          slug
          sport
          type
          ...MatchCenterPage_so5Fixture
        }
      }
    }
  }
  ${MatchCenterPage.fragments.so5Fixture}
` as TypedDocumentNode<NBAScoresQuery, NBAScoresQueryVariables>;

const ScoresPage = withRouteQuery(
  ({ queryResult }) => {
    const [gameId, setGameId] = useState<string>();
    const [playerGameScoreId, setPlayerGameScoreId] = useState<string>();
    const navigate = useNavigate();
    const track = useAnySportEvents();
    const isLaptopAndAbove = useIsLaptop();

    const { so5Fixtures } = queryResult.data.so5 || {};

    const sortedFixtures = [...(so5Fixtures?.nodes || [])].sort((a, b) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

    return (
      <>
        <ProfilePageHeader />
        <MatchCenterPage
          withAllGames
          withFixtureHeader={false}
          so5Fixtures={sortedFixtures}
          onClickGame={id => {
            track('Open Match View', {
              gameId: id,
              deviceType: `web_${isLaptopAndAbove ? 'desktop' : 'mobile'}`,
              interactionContext: 'match_center',
            });
            navigate(generatePath(NBA_SCORES_GAMEID, { gameId: id }));
          }}
          onClickPlayerGameScore={(scoreId, playerSlug) => {
            track('Open Match Center Appearance Details', {
              playerSlug,
            });
            setPlayerGameScoreId(scoreId);
          }}
        />
        <>
          {gameId && (
            <MatchViewDialog
              open
              onClose={() => setGameId(undefined)}
              gameId={gameId}
            />
          )}
          {playerGameScoreId && (
            <PlayerGameScoreDrawer
              open
              onClose={() => setPlayerGameScoreId(undefined)}
              playerGameScoreId={playerGameScoreId}
              so5FixtureSlug={sortedFixtures[0].slug}
              withListingsFooter
            />
          )}
        </>
      </>
    );
  },
  {
    query: NBA_SCORES_QUERY,
  }
);

export default ScoresPage;
